import { createContext, useContext, useEffect, useState } from 'react';
import {
  APPEARANCE_STORAGE_KEY,
  DEFAULT_APPEARANCE,
  applyAppearance,
  readStoredAppearance,
  type Appearance,
} from '@/lib/appearance';

/**
 * User-selectable theme. 'system' is a user *choice* that resolves to one of the
 * concrete themes — the CSS itself never branches on prefers-color-scheme, it only
 * ever keys off the resolved `data-theme` attribute on <html>.
 */
type Theme = 'light' | 'dark' | 'gray' | 'system';

/** Themes that map directly to a `data-theme` value. */
type ResolvedTheme = 'light' | 'dark' | 'gray';

export const THEME_STORAGE_KEY = 'synapse-theme';

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: ResolvedTheme;
  appearance: Appearance;
  /** Patch one or more appearance fields; the rest are preserved. */
  setAppearance: (patch: Partial<Appearance>) => void;
  resetAppearance: () => void;
};

const initialState: ThemeProviderState = {
  theme: 'system',
  setTheme: () => null,
  resolvedTheme: 'light',
  appearance: DEFAULT_APPEARANCE,
  setAppearance: () => null,
  resetAppearance: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

const prefersDark = () => window.matchMedia('(prefers-color-scheme: dark)').matches;

const resolve = (theme: Theme): ResolvedTheme =>
  theme === 'system' ? (prefersDark() ? 'dark' : 'light') : theme;

/**
 * Writes the resolved theme to <html data-theme="...">. Light is represented by
 * the attribute too (rather than its absence) so the value always round-trips.
 */
function applyTheme(resolved: ResolvedTheme) {
  document.documentElement.setAttribute('data-theme', resolved);
}

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = THEME_STORAGE_KEY,
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme
  );

  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolve((localStorage.getItem(storageKey) as Theme) || defaultTheme)
  );

  useEffect(() => {
    const resolved = resolve(theme);
    applyTheme(resolved);
    setResolvedTheme(resolved);
  }, [theme]);

  // Only follow the OS while the user has explicitly chosen 'system'.
  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      const resolved: ResolvedTheme = mediaQuery.matches ? 'dark' : 'light';
      applyTheme(resolved);
      setResolvedTheme(resolved);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  // Appearance (brand colours, nav rail, AI card) is independent of light/dark:
  // a user keeps their colours when switching mode.
  const [appearance, setAppearanceState] = useState<Appearance>(readStoredAppearance);

  // Re-applied on theme change too — several values differ per mode.
  useEffect(() => {
    applyAppearance(appearance, resolvedTheme !== 'light');
  }, [appearance, resolvedTheme]);

  const value = {
    theme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme);
      setTheme(theme);
    },
    resolvedTheme,
    appearance,
    setAppearance: (patch: Partial<Appearance>) => {
      setAppearanceState((prev) => {
        const next = { ...prev, ...patch };
        localStorage.setItem(APPEARANCE_STORAGE_KEY, JSON.stringify(next));
        return next;
      });
    },
    resetAppearance: () => {
      localStorage.removeItem(APPEARANCE_STORAGE_KEY);
      setAppearanceState(DEFAULT_APPEARANCE);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined) throw new Error('useTheme must be used within a ThemeProvider');

  return context;
};
